import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLoginSuperAdmin } from "http-hooks/superAdmin/useSuperAdminAuth";
import Input from "components/ui/Input";
import logo from "assets/img/logo.png";

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  const { mutateAsync: loginSuperAdmin, isPending } = useLoginSuperAdmin();

  const updateCredentials = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = async () => {
    if (!credentials.username || !credentials.password) {
      return;
    }

    try {
      await loginSuperAdmin(credentials);
      navigate("/super-admin/companies");
    } catch (error) {
      // Error is already handled by the service with toast
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <img
            className="h-16 w-auto mx-auto mb-6"
            src={logo}
            alt="Company Logo"
          />
          <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-purple-900/50 border border-purple-500/50 mb-4">
            <svg
              className="h-5 w-5 text-purple-400 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <span className="text-purple-200 text-sm font-medium">
              Super Admin Access
            </span>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 text-center">
              Super Administrator Portal
            </h3>
            <p className="text-gray-600 text-sm text-center mt-1">
              Manage companies, databases, and system configurations
            </p>
          </div>

          <form className="space-y-5" onKeyPress={handleKeyPress}>
            <Input
              id="username"
              name="username"
              type="text"
              label="Username"
              value={credentials.username}
              onChange={updateCredentials}
              placeholder="Enter super admin username"
              autoComplete="username"
              isRequired
            />

            <Input
              id="password"
              name="password"
              type="password"
              label="Password"
              value={credentials.password}
              onChange={updateCredentials}
              placeholder="Enter super admin password"
              autoComplete="current-password"
              isRequired
            />

            {/* Login Button */}
            <button
              type="button"
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
              onClick={handleLogin}
              disabled={isPending}
            >
              {isPending ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Authenticating...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  Access Super Admin Portal
                </div>
              )}
            </button>
          </form>

          {/* Warning Message */}
          <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm text-purple-800 font-medium">
                  Restricted Access
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  This portal is for authorized super administrators only. All
                  actions are logged and monitored.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-purple-200 text-xs">
            © 2024{" "}
            {process.env.REACT_APP_BRAND_NAME || "Business Management System"}.
            All rights reserved.
          </p>
          <button
            onClick={() => navigate("/")}
            className="text-purple-300 hover:text-purple-100 text-xs mt-2 transition-colors"
          >
            ← Back to Regular Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
